import React from 'react';
import { Typography, Paper, Box, List, ListItemButton, ListItemText, LinearProgress, Breadcrumbs, Link as MuiLink, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { formatCurrency } from '../../utils/formatters';
import { formatDate } from '../../utils/dateUtils';

interface ObraAnaliseTabProps {
    title: string;
    custos: any[];
    lancamentos: any[] | null;
    breadcrumbs: any[];
    selectedInfo: any;
    onItemClick: (item: any) => void;
    onBreadcrumbClick: (index: number) => void;
    onOpenAttachments: (anexos: any[]) => void;
}

export const ObraAnaliseTab: React.FC<ObraAnaliseTabProps> = ({
    title,
    custos,
    lancamentos,
    breadcrumbs,
    selectedInfo,
    onItemClick,
    onBreadcrumbClick,
    onOpenAttachments
}) => {

    const renderBreadcrumbs = () => (
        <Paper elevation={0} sx={{ p: 1, mb: 2, bgcolor: 'grey.100' }}>
            <Breadcrumbs aria-label="breadcrumb">
                <MuiLink component="button" underline="hover" color="inherit" onClick={() => onBreadcrumbClick(0)} sx={{ display: 'flex', alignItems: 'center' }}>
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Raiz
                </MuiLink>
                {breadcrumbs.map((crumb, index) => (
                    <MuiLink component="button" underline="hover" color="inherit" key={crumb.id} onClick={() => onBreadcrumbClick(index + 1)}>
                        {crumb.nome}
                    </MuiLink>
                ))}
                {selectedInfo && <Typography color="text.primary">{selectedInfo.nome}</Typography>}
            </Breadcrumbs>
        </Paper>
    );

    const renderCustosList = () => {
        if (custos.length === 0) return null;

        return (
            <Paper sx={{ p: 2 }}>
                <List disablePadding>
                    {custos.map((item) => (
                        <ListItemButton key={item.id} onClick={() => onItemClick(item)} divider>
                            <ListItemText
                                primary={item.nome}
                                secondary={
                                    <Typography variant="body2" color="text.secondary">
                                        {formatCurrency(item.total)}
                                    </Typography>
                                }
                            />
                            <Box sx={{ width: '40%', ml: 2, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress variant="determinate" value={item.percentual} sx={{ height: 8, borderRadius: 4 }} />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                    <Typography variant="body2" color="text.secondary">{`${Math.round(item.percentual)}%`}</Typography>
                                </Box>
                            </Box>
                        </ListItemButton>
                    ))}
                </List>
            </Paper>
        );
    };

    const renderLancamentosTable = () => {
        if (!lancamentos) return null;
        const allItems = lancamentos.flatMap(l => l.items.map((item: any) => ({ ...item, lancamento: l })));

        return (
            <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Itens de Lançamento</Typography>
                {allItems.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Categoria</TableCell>
                                    <TableCell>Fornecedor</TableCell>
                                    <TableCell align="right">Qtd.</TableCell>
                                    <TableCell align="right">Vlr. Unit. (Bruto)</TableCell>
                                    <TableCell align="right">Desc. Unit.</TableCell>
                                    <TableCell align="right">Vlr. Unit. (Líq.)</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="center">Anexos</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allItems.map((item: any) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell>{formatDate(item.lancamento.data_lancamento)}</TableCell>
                                        <TableCell>{item.descricao}</TableCell>
                                        <TableCell>{item.categoria_nome}</TableCell>
                                        <TableCell>{item.lancamento.fornecedor_nome}</TableCell>
                                        <TableCell align="right">{item.quantidade}</TableCell>
                                        <TableCell align="right">{formatCurrency(item.valor_unitario)}</TableCell>
                                        <TableCell align="right">
                                            {(() => {
                                                const totalBruto = item.quantidade * item.valor_unitario;
                                                const descontoValor = item.desconto_valor || 0;
                                                const descontoPercentual = item.desconto_percentual || 0;
                                                const totalDescontoItem = descontoValor + (totalBruto * descontoPercentual / 100);

                                                const totalLiquido = item.valor_liquido_proporcional ?? (totalBruto - totalDescontoItem);
                                                const totalDescontoEfetivo = totalBruto - totalLiquido;

                                                // Calculate Unit Discount
                                                const unitDesconto = item.quantidade > 0 ? totalDescontoEfetivo / item.quantidade : 0;

                                                return formatCurrency(unitDesconto);
                                            })()}
                                        </TableCell>
                                        <TableCell align="right">
                                            {(() => {
                                                const totalBruto = item.quantidade * item.valor_unitario;
                                                const descontoValor = item.desconto_valor || 0;
                                                const descontoPercentual = item.desconto_percentual || 0;
                                                const totalDescontoItem = descontoValor + (totalBruto * descontoPercentual / 100);

                                                // Use prorated value if available, otherwise fallback to item discount
                                                const totalLiquido = item.valor_liquido_proporcional ?? (totalBruto - totalDescontoItem);

                                                // Calculate Unit Net Price
                                                const unitLiquido = item.quantidade > 0 ? totalLiquido / item.quantidade : 0;

                                                return formatCurrency(unitLiquido);
                                            })()}
                                        </TableCell>
                                        <TableCell align="right">
                                            {(() => {
                                                const totalBruto = item.quantidade * item.valor_unitario;
                                                const descontoValor = item.desconto_valor || 0;
                                                const descontoPercentual = item.desconto_percentual || 0;
                                                const totalDescontoItem = descontoValor + (totalBruto * descontoPercentual / 100);

                                                // Use prorated value if available, otherwise fallback to item discount
                                                const totalLiquido = item.valor_liquido_proporcional ?? (totalBruto - totalDescontoItem);

                                                return formatCurrency(totalLiquido);
                                            })()}
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.lancamento.anexos && item.lancamento.anexos.length > 0 && (
                                                <Tooltip title="Ver Anexos">
                                                    <IconButton size="small" onClick={() => onOpenAttachments(item.lancamento.anexos)}>
                                                        <AttachFileIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body1" color="textSecondary">Nenhum item encontrado nesta categoria.</Typography>
                    </Paper>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>{title}</Typography>

            {renderBreadcrumbs()}

            {custos.length > 0 ? renderCustosList() : renderLancamentosTable()}

            {custos.length === 0 && (!lancamentos || lancamentos.length === 0) && !selectedInfo && (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="textSecondary">Nenhum custo encontrado.</Typography>
                </Paper>
            )}
        </Box>
    );
};
